import {
  Button,
  FormInput,
  FormRoot,
  Modal,
  RHF,
  toast,
  zodResolver,
} from "@devhop/ui";
import { XIcon } from "lucide-react";
import { useEffect } from "react";
import type { Resolver } from "react-hook-form";
import { z } from "zod";
import type { MemberTierDTO } from "../api/client";
import { useCreateMemberTier, useUpdateMemberTier } from "../api/queries";

const MemberTierFormSchema = z.object({
  nameLo: z.string().optional(),
  nameTh: z.string().optional(),
  nameEn: z.string().optional(),
  sortOrder: z.coerce.number().int().min(0).default(0),
});

type MemberTierFormValues = z.infer<typeof MemberTierFormSchema>;

function toFormValues(tier?: MemberTierDTO | null): MemberTierFormValues {
  if (!tier) return { nameLo: "", nameTh: "", nameEn: "", sortOrder: 0 };
  const n = tier.name as { lo?: string; th?: string; en?: string };
  return {
    nameLo: n?.lo ?? "",
    nameTh: n?.th ?? "",
    nameEn: n?.en ?? "",
    sortOrder: tier.sortOrder ?? 0,
  };
}

type MemberTierFormDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tier?: MemberTierDTO | null;
};

export function MemberTierFormDialog({
  open,
  onOpenChange,
  tier,
}: MemberTierFormDialogProps) {
  const isEdit = !!tier;
  const createTier = useCreateMemberTier();
  const updateTier = useUpdateMemberTier();

  const methods = RHF.useForm<MemberTierFormValues>({
    resolver: zodResolver(
      MemberTierFormSchema,
    ) as Resolver<MemberTierFormValues>,
    defaultValues: toFormValues(tier),
  });

  useEffect(() => {
    if (open) {
      methods.reset(toFormValues(tier));
    }
  }, [open, tier?.id]);

  const onSubmit = async (vals: MemberTierFormValues) => {
    const data = {
      name: {
        lo: vals.nameLo || undefined,
        th: vals.nameTh || undefined,
        en: vals.nameEn || undefined,
      },
      sortOrder: vals.sortOrder,
    };

    if (isEdit && tier) {
      await toast.promise(updateTier.mutateAsync({ id: tier.id, data }), {
        loading: "ກຳລັງບັນທຶກ...",
        success: "ບັນທຶກສຳເລັດ",
        error: "ບັນທຶກລົ້ມເຫຼວ",
      });
    } else {
      await toast.promise(createTier.mutateAsync(data), {
        loading: "ກຳລັງບັນທຶກ...",
        success: "ສ້າງລະດັບສະມາຊິກສຳເລັດ",
        error: "ສ້າງລົ້ມເຫຼວ",
      });
    }
    onOpenChange(false);
    methods.reset();
  };

  const isPending = createTier.isPending || updateTier.isPending;

  return (
    <Modal open={open} onOpenChange={onOpenChange} showCloseButton={false}>
      <FormRoot methods={methods} onSubmit={onSubmit}>
        <div className="space-y-5">
          <div className="flex items-start justify-between border-b pb-4">
            <h3 className="font-semibold text-lg">
              {isEdit ? "ແກ້ໄຂລະດັບສະມາຊິກ" : "ສ້າງລະດັບສະມາຊິກ"}
            </h3>
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              aria-label="ປິດ"
            >
              <XIcon className="h-5 w-5" />
            </button>
          </div>

          <FormInput name="nameLo" label="ຊື່ (ລາວ)" placeholder="ໃສ່ຊື່ພາສາລາວ" />
          <FormInput name="nameTh" label="ຊື່ (ໄທ)" placeholder="ໃສ່ຊື່ພາສາໄທ" />
          <FormInput name="nameEn" label="ຊື່ (ອັງກິດ)" placeholder="ໃສ່ຊື່ພາສາອັງກິດ" />
          <FormInput name="sortOrder" label="ລຳດັບ" type="number" />

          <div className="flex justify-end gap-2 border-t pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isPending}
            >
              ຍົກເລີກ
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending ? "ກຳລັງບັນທຶກ..." : "ບັນທຶກ"}
            </Button>
          </div>
        </div>
      </FormRoot>
    </Modal>
  );
}
